import { useFormContext, useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useCallback, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OptionsList, SearchableSelectField } from 'components/core/form';
import { translateRole } from 'features/invite_flow/utils';
import { UserGroupPopup } from 'features/user_groups';
import { useBooleanState } from 'hooks/useBooleanState';
import { usePrevious } from 'hooks/usePrevious';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { anyGroupsSelector, isUserGroupsFeatureEnabledSelector, selectedGroupNameSelector, selectedGroupSelector, userGroupsLoadingSelector, userGroupsSelector } from '../../selectors';
import { createAndSelectUserGroup, fetchUserGroups, selectUserGroup } from '../../slice';
const userGroupContainerCss = "udu6obv";
const UserGroupContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(userGroupContainerCss, props.className)} />;
});
const subHeaderContainerCss = "s12fgz26";
const SubHeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subHeaderContainerCss, props.className)} />;
});
export const GroupSection = () => {
  const dispatch = useDispatch();
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const userGroups = useSelector(userGroupsSelector);
  const anyGroups = useSelector(anyGroupsSelector);
  const isLoading = useSelector(userGroupsLoadingSelector);
  const selectedGroup = useSelector(selectedGroupSelector);
  const selectedGroupName = useSelector(selectedGroupNameSelector);
  const isUserGroupsFeatureEnabled = useSelector(isUserGroupsFeatureEnabledSelector);
  const showSelectGroups = isUserGroupsFeatureEnabled || anyGroups;
  const [isOpen, openPopup, closePopup] = useBooleanState(false);
  const [{
    value: groupId
  }, _meta, {
    setValue,
    setTouched
  }] = useFormField({
    name: 'groupId'
  });
  const prevGroupId = usePrevious(groupId);
  const {
    values: {
      invitations
    },
    setFieldValue,
    setFieldTouched
  } = useFormContext();
  useEffect(() => {
    dispatch(selectUserGroup(groupId));
    if (prevGroupId === undefined || prevGroupId === groupId) {
      return;
    }
    if (!invitations.length) {
      return;
    }
    const [role, userGroupRole] = translateRole('user');
    setFieldTouched('invitations', true);
    invitations.forEach((_, index) => {
      setFieldValue(`invitations.${index}.displayRole`, 'user');
      setFieldValue(`invitations.${index}.role`, role);
      setFieldValue(`invitations.${index}.userGroupRole`, userGroupRole);
      setFieldValue(`invitations.${index}.userGroupId`, groupId);
    });
  }, [dispatch, groupId, prevGroupId, setFieldTouched, setFieldValue, invitations]);
  useEffect(() => {
    if (!selectedGroup) {
      if (isAdminOrOwner) return;
      const defaultGroupId = userGroups[0]?.id;
      if (!defaultGroupId) return;
      dispatch(selectUserGroup(defaultGroupId));
      setValue(defaultGroupId);
      return;
    }
    setTouched(true);
    setValue(selectedGroup.id);
  }, [selectedGroup, setValue, setTouched, isAdminOrOwner, userGroups, dispatch]);
  const handleGroupPopup = params => dispatch(createAndSelectUserGroup(params));
  const handleSearch = useCallback(query => {
    dispatch(fetchUserGroups(query));
  }, [dispatch]);
  const sectionTitle = () => {
    if (isAdminOrOwner) {
      return I18n.t('publisher.organization.invite_flow.add_emails_step.group_select.title');
    }
    return I18n.t('publisher.organization.invite_flow.add_emails_step.group_select.title_not_optional');
  };
  const optionsForDropdown = () => {
    const optionsList = [];
    if (isAdminOrOwner && userGroups.length > 0) {
      optionsList.push({
        id: null,
        name: I18n.t('publisher.organization.invite_flow.add_emails_step.group_select.empty_group_select')
      });
    }
    return optionsList.concat(userGroups);
  };
  return <>
      {showSelectGroups && <>
        <SubHeaderContainer>
          <BodyText weight="bold">{sectionTitle()}</BodyText>
          <BodyText>{I18n.t('publisher.organization.invite_flow.add_emails_step.group_select.description')}</BodyText>
        </SubHeaderContainer>
        <SearchableSelectField name="groupId" aria-label={I18n.t('publisher.organization.members.add_to_group.select_group_placeholder')} options={optionsForDropdown()} placeholder={I18n.t('publisher.organization.invite_flow.add_emails_step.group_select.placeholder')} displayValue={selectedGroupName} onSearch={handleSearch}>
          <OptionsList isLoading={isLoading} notFoundText={I18n.t('publisher.organization.members.add_to_group.no_groups_found')} />
        </SearchableSelectField>
      </>}
      {isUserGroupsFeatureEnabled && isAdminOrOwner && <UserGroupContainer>
        <MinimalButton onClick={openPopup}>
          {I18n.t('publisher.organization.invite_flow.add_emails_step.create_group')}
        </MinimalButton>
        <UserGroupPopup isOpen={isOpen} title={I18n.t('publisher.organization.user_groups.form.title')} confirmLabel={I18n.t('publisher.action.continue')} initialValues={{
        name: ''
      }} onSubmit={handleGroupPopup} onClose={closePopup} />
      </UserGroupContainer>}
    </>;
};

require("./group_section.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./group_section.js");